/**
 * Férias do colaborador, no formato que o backend devolve (Employee / PublicEmployeeDTO):
 * vacationEnabled, vacationStartDate e vacationReturnDate como "YYYY-MM-DD".
 *
 * O período é [saída, volta): o dia da volta já é dia de atendimento, que é como a
 * recepção fala ("volta dia 15"). As datas são comparadas como texto no formato ISO
 * para não sofrer deslocamento de fuso ao virar Date.
 */

/** Data local em "YYYY-MM-DD", sem passar por UTC. */
const toIsoDay = (date) => {
    if (!date) {
        return null;
    }

    if (typeof date === 'string') {
        const match = date.match(/^(\d{4}-\d{2}-\d{2})/);
        return match ? match[1] : null;
    }

    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime())) {
        return null;
    }

    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
};

/** "2026-10-15" vira "15/10/2026". */
const toBrazilianDate = (isoDay) => {
    const [year, month, day] = String(isoDay).slice(0, 10).split('-');
    return `${day}/${month}/${year}`;
};

/** Véspera de uma data ISO, usada para mostrar o último dia fora. */
const previousIsoDay = (isoDay) => {
    const [year, month, day] = String(isoDay).slice(0, 10).split('-').map(Number);
    return toIsoDay(new Date(year, month - 1, day - 1));
};

/**
 * Férias ligadas e com período completo.
 *
 * O site público recebe as datas só quando as férias estão ligadas, sem o campo
 * vacationEnabled; por isso a ausência dele não conta como desligado.
 */
export const hasVacationPeriod = (employee) => Boolean(
    employee
    && employee.vacationEnabled !== false
    && employee.vacationStartDate
    && employee.vacationReturnDate
    && String(employee.vacationReturnDate) > String(employee.vacationStartDate)
);

/** O colaborador está de férias na data informada? */
export const isOnVacationOn = (employee, date) => {
    const day = toIsoDay(date);
    if (!day || !hasVacationPeriod(employee)) {
        return false;
    }

    const start = String(employee.vacationStartDate).slice(0, 10);
    const back = String(employee.vacationReturnDate).slice(0, 10);
    return day >= start && day < back;
};

/** Rótulo curto do período: "01/10/2026 a 14/10/2026". */
export const vacationPeriodLabel = (employee) => {
    if (!hasVacationPeriod(employee)) {
        return '';
    }

    return `${toBrazilianDate(employee.vacationStartDate)} a ${toBrazilianDate(previousIsoDay(employee.vacationReturnDate))}`;
};

/** Último dia fora (véspera da volta), formatado: "14/10/2026". */
export const vacationLastDayLabel = (employee) =>
    hasVacationPeriod(employee) ? toBrazilianDate(previousIsoDay(employee.vacationReturnDate)) : '';

/** Dia da volta formatado: "15/10/2026". */
export const vacationReturnLabel = (employee) =>
    hasVacationPeriod(employee) ? toBrazilianDate(employee.vacationReturnDate) : '';

/**
 * Situação das férias em relação a hoje, para o cadastro e a listagem.
 *
 * "scheduled" = ainda vai sair, "active" = está fora hoje, "finished" = já voltou
 * (as férias continuam ligadas, mas não fecham mais nada), null = desligadas.
 */
export const vacationStatus = (employee, today = new Date()) => {
    if (!hasVacationPeriod(employee)) {
        return null;
    }

    const day = toIsoDay(today);
    if (day < String(employee.vacationStartDate).slice(0, 10)) {
        return 'scheduled';
    }

    return isOnVacationOn(employee, today) ? 'active' : 'finished';
};

/** Mensagem única de recusa, igual à que o backend devolve ao salvar. */
export const vacationMessage = (employee, name) =>
    `${name || 'O colaborador'} está de férias de ${vacationPeriodLabel(employee)} e volta a atender em ${vacationReturnLabel(employee)}.`;
